/**
 * Composant Mes Documents pour l'espace Membre
 * Affiche les documents du membre : attestations FTPE, RCDS, devis, etc.
 * Permet le téléchargement des documents PDF
 */
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { VisionneuseProtegee } from './documents/VisionneuseProtegee';
import { PreuveVirementDepot } from './documents/PreuveVirementDepot';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileText, Download, Eye, RefreshCw, Loader2, Shield, AlertTriangle, FolderOpen, Calendar, FileCheck, PlayCircle } from 'lucide-react';
import { useMesDocumentsMembre } from './useMesDocumentsMembre';
import { getDocumentIcon, getStatusBadge, getTypeLabel, formatDate } from './mesDocumentsHelpers';
import MesDocumentsSections from './MesDocumentsSections';
import { PropositionSignatureAction } from './DocumentSections/PropositionSignatureAction';

export default function MesDocumentsMembre({ membreId }) {
  const {
    navigate, documentsGed, souscriptionsEnCours, recusPaiements, documentsAdmin,
    categories, loading, downloadingId, activeFilter, setActiveFilter,
    fetchDocuments, handleDownload, handleView, handleDownloadRecu,
    deduplicatedDocuments, filteredDocuments,
  } = useMesDocumentsMembre(membreId);
  const [protege, setProtege] = useState(null);
  const { user } = useAuth() || {};
  const estAdmin = ['admin', 'super_admin'].includes((user?.role || '').toLowerCase());
  const verrouille = (doc) => doc.en_validation && !estAdmin;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Chargement de vos documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FolderOpen className="h-7 w-7 text-amber-600" />
            Mes Documents
          </h2>
          <p className="text-gray-500 mt-1">
            Vos attestations, devis et documents officiels
          </p>
        </div>
        <Button onClick={fetchDocuments} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Filtres par catégorie */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveFilter('all')}
          data-testid="filter-all"
        >
          Tous ({deduplicatedDocuments.length})
        </Button>
        {(categories.ftpe > 0 || categories.ftpe_signees > 0 || categories.ftpe_non_signees > 0 || categories.ftpe_en_cours > 0) && (
          <Button
            variant={activeFilter === 'attestation_ftpe' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('attestation_ftpe')}
            className={activeFilter === 'attestation_ftpe' ? 'bg-blue-600' : ''}
            data-testid="filter-ftpe"
          >
            <Shield className="h-4 w-4 mr-1" />
            FTPE ({categories.ftpe})
            {categories.ftpe_signees > 0 && (
              <Badge className="ml-1 bg-emerald-600 text-white text-xs px-1 font-semibold">{categories.ftpe_signees}</Badge>
            )}
            {categories.ftpe_non_signees > 0 && (
              <Badge className="ml-1 bg-amber-600 text-white text-xs px-1 font-semibold">{categories.ftpe_non_signees}</Badge>
            )}
          </Button>
        )}
        {categories.rcds > 0 && (
          <Button
            variant={activeFilter === 'attestation_rcds' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('attestation_rcds')}
            className={activeFilter === 'attestation_rcds' ? 'bg-green-600' : ''}
          >
            <FileCheck className="h-4 w-4 mr-1" />
            RCDS ({categories.rcds})
          </Button>
        )}
        {categories.devis > 0 && (
          <Button
            variant={activeFilter === 'devis_ess' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('devis_ess')}
            className={activeFilter === 'devis_ess' ? 'bg-purple-600' : ''}
          >
            <FileText className="h-4 w-4 mr-1" />
            Devis ({categories.devis})
          </Button>
        )}
        {categories.ged > 0 && (
          <Button
            variant={activeFilter === 'ged' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('ged')}
            className={activeFilter === 'ged' ? 'bg-indigo-600' : ''}
          >
            <FolderOpen className="h-4 w-4 mr-1" />
            GED ({categories.ged})
          </Button>
        )}
      </div>

      {/* Liste des documents */}
      {filteredDocuments.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun document
            </h3>
            <p className="text-gray-500">
              {activeFilter === 'all'
                ? 'Vous n\'avez pas encore de documents disponibles.'
                : `Aucun document de type "${getTypeLabel(activeFilter)}" disponible.`
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDocuments.map((doc) => (
            <Card
              key={doc.id}
              data-testid={`document-card-${doc.id}`}
              className={`transition-all hover:shadow-md ${
                !doc.is_valid ? 'opacity-60 border-red-200' : ''
              } ${
                doc.document_status === 'non_signee' ? 'border-amber-300 bg-amber-50/30' : ''
              } ${
                doc.document_status === 'en_cours' ? 'border-blue-300 bg-blue-50/30' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Icone */}
                  <div className={`p-3 rounded-lg ${
                    doc.type === 'attestation_ftpe' ? 'bg-blue-100' :
                    doc.type === 'attestation_rcds' ? 'bg-green-100' :
                    doc.type === 'devis_ess' ? 'bg-purple-100' :
                    'bg-gray-100'
                  }`}>
                    {getDocumentIcon(doc.type)}
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900 truncate">
                        {doc.nom}
                      </h4>
                      {getStatusBadge(doc)}
                      {(doc.metadata && doc.metadata.reprise_simulateur) && (
                        <Badge
                          variant="outline"
                          className="border-amber-400 text-amber-700 bg-amber-50 text-[10px] whitespace-nowrap"
                          data-testid={`doc-reprise-badge-${doc.id}`}
                        >
                          <RefreshCw className="h-2.5 w-2.5 mr-1" />
                          Reprise simulateur
                        </Badge>
                      )}
                    </div>

                    {doc.description && (
                      <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                        {doc.description}
                      </p>
                    )}
                    {doc.en_validation && doc.type === 'attestation_rcds' && doc.souscription_id && (
                      <div className="my-2"><PreuveVirementDepot souscriptionId={doc.souscription_id} /></div>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Cree le {formatDate(doc.date_creation)}
                      </span>
                      {doc.date_expiration && (
                        <span className={`flex items-center gap-1 ${
                          !doc.is_valid ? 'text-red-500' : ''
                        }`}>
                          <AlertTriangle className="h-3 w-3" />
                          Expire le {formatDate(doc.date_expiration)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {(doc.can_download || doc.url || doc.souscription_id || doc.adhesion_id) && (
                      <Button
                        data-testid={`view-btn-${doc.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => (verrouille(doc) ? setProtege(doc) : handleView(doc))}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir
                      </Button>
                    )}
                    {!verrouille(doc) && (doc.can_download || doc.url || doc.souscription_id || doc.adhesion_id) && (
                      <Button
                        data-testid={`download-btn-${doc.id}`}
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        disabled={downloadingId === doc.id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {downloadingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </>
                        )}
                      </Button>
                    )}
                    {doc.type === 'devis_ess_genere' && (
                      <PropositionSignatureAction devisId={doc.reference || doc.metadata?.devis_id} docId={doc.id} expired={!doc.is_valid} />
                    )}
                    {(doc.peut_finaliser || (doc.document_status === 'non_signee' && doc.can_resume)) && (
                      <Button
                        data-testid={`finalize-btn-${doc.id}`}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => {
                          if (doc.adhesion_id) {
                            navigate(`/ftpe/adhesion?resume=${doc.adhesion_id}`);
                          }
                        }}
                      >
                        <PlayCircle className="h-4 w-4 mr-1" />
                        Finaliser
                      </Button>
                    )}
                    {doc.document_status === 'en_cours' && doc.can_resume && (
                      <Button
                        data-testid={`resume-btn-${doc.id}`}
                        size="sm"
                        variant="outline"
                        className="border-blue-500 text-blue-700 hover:bg-blue-100"
                        onClick={() => {
                          if (doc.adhesion_id) {
                            navigate(`/ftpe/adhesion?resume=${doc.adhesion_id}`);
                          }
                        }}
                      >
                        <PlayCircle className="h-4 w-4 mr-1" />
                        Reprendre
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <MesDocumentsSections
        souscriptionsEnCours={souscriptionsEnCours}
        recusPaiements={recusPaiements}
        documentsGed={documentsGed}
        documentsAdmin={documentsAdmin}
        categories={categories}
        activeFilter={activeFilter}
        downloadingId={downloadingId}
        handleDownloadRecu={handleDownloadRecu}
        handleView={handleView}
        handleDownload={handleDownload}
      />
      {protege && (
        <VisionneuseProtegee url={`${process.env.REACT_APP_BACKEND_URL}${protege.url}${protege.url.includes('?') ? '&' : '?'}preview=true`}
          titre={`${protege.nom} — ${protege.description || ''}`} onClose={() => setProtege(null)} />
      )}
    </div>
  );
}
